'use client';

/**
 * ToastPolicy — at most ONE error toast on screen, portal-wide (2026-09-25).
 *
 * One failure used to produce two toasts: the API client's specific message
 * ("You don't have permission to do that.") and the page's generic catch-block
 * toast ("Failed to delete schedule"). Rather than patch react-hot-toast, this
 * watches the toast store and removes the extras:
 *   - the API client's toast wins when the others arrived within 2 s of it
 *     (same failure — it has the specific reason);
 *   - otherwise the newest error wins (a new failure replaces an old one).
 * Mounted once, next to the single bottom-right <Toaster> in app/layout.tsx.
 */
import { useEffect } from 'react';
import toast, { useToasterStore, type Toast } from 'react-hot-toast';
import { API_ERROR_TOAST_ID } from '@/lib/api-client';

const SAME_FAILURE_MS = 2000;

/** Ids of error toasts to remove so that exactly one stays. Pure for tests. */
export function errorToastsToRemove(toasts: Pick<Toast, 'id' | 'type' | 'visible' | 'createdAt'>[]): string[] {
  const errors = toasts.filter((t) => t.type === 'error' && t.visible);
  if (errors.length <= 1) return [];
  const newest = errors.reduce((a, b) => (b.createdAt >= a.createdAt ? b : a));
  const api = errors.find((t) => t.id === API_ERROR_TOAST_ID);
  const keep = api && errors.every((t) => Math.abs(t.createdAt - api.createdAt) < SAME_FAILURE_MS) ? api : newest;
  return errors.filter((t) => t.id !== keep.id).map((t) => t.id);
}

export function ToastPolicy() {
  const { toasts } = useToasterStore();
  useEffect(() => {
    for (const id of errorToastsToRemove(toasts)) toast.remove(id);
  }, [toasts]);
  return null;
}
